'use client'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { Customer, DateFields, Traveler } from '@/src/types/itinerary'
import { DeviceType } from '@/src/types/deviceType'
import { dateObjectToIsoString } from '@/src/utils/itinerary/dateTransformer'
import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { TravellerReviewOverlay, TravellerDisplayItem, ContactDisplay } from './travellerReviewOverlay'

interface TravellerReviewOverlayWrapperProps {
    open: boolean
    onClose: () => void
    onConfirm: () => Promise<void>
    isLoading: boolean
    travelers: Traveler[]
    contactDetails: Customer
    isDateOfBirthRequired: boolean
    deviceType: DeviceType
    travelersIndexToPassengerTypeAndSerialNumberMap: Record<
        number,
        { type: string; count: number; typeString: string }
    >
}

const TravellerReviewOverlayWrapper = ({
    open,
    onClose,
    onConfirm,
    isLoading,
    travelers,
    contactDetails,
    isDateOfBirthRequired,
    deviceType,
    travelersIndexToPassengerTypeAndSerialNumberMap
}: TravellerReviewOverlayWrapperProps) => {
    const t = useTranslations()
    const titlesTranslate = useTranslations('common.titles')
    const { formatDate } = useFormatDate()

    const formatDisplayDate = useCallback(
        (date: DateFields | string | null | undefined): string | undefined => {
            if (!date) return undefined
            if (typeof date === 'string') {
                return date ? formatDate(date, 'DD MMMM YYYY') : undefined
            }
            const { day, month, year } = date
            if (!day || !month || !year) return undefined
            return formatDate(dateObjectToIsoString(date), 'DD MMMM YYYY')
        },
        [formatDate]
    )

    const contact: ContactDisplay = useMemo(() => {
        const { mobileCountryCode, mobile, email } = contactDetails
        const phone = mobileCountryCode && mobile ? `+${mobileCountryCode} ${mobile}` : mobile || ''
        return { phone, email: email || '' }
    }, [contactDetails])

    const travellerItems: TravellerDisplayItem[] = useMemo(
        () =>
            travelers.map((traveler, idx) => {
                const map = travelersIndexToPassengerTypeAndSerialNumberMap[idx]
                const label = t(
                    `itineraryPage.travelerDetailsRecord.travelerType.${map.typeString}`,
                    { number: map.count }
                )

                const titleStr = traveler.title
                    ? `${titlesTranslate(traveler.title.toLowerCase())} `
                    : ''
                const name = `${titleStr}${traveler.firstName} ${traveler.lastName}`.trim()

                const dob =
                    isDateOfBirthRequired && traveler.dateOfBirth
                        ? formatDisplayDate(traveler.dateOfBirth)
                        : undefined

                const docType = traveler.selectedDocumentType
                const docDetails = docType ? traveler.documentDetails?.[docType] : undefined

                const docNumber = docDetails?.docNumber
                    ? String(docDetails.docNumber) || undefined
                    : undefined

                const docExpiry = docDetails?.expiryDate
                    ? formatDisplayDate(docDetails.expiryDate)
                    : undefined

                return { label, name, dob, docNumber, docExpiry }
            }),
        [
            travelers,
            travelersIndexToPassengerTypeAndSerialNumberMap,
            isDateOfBirthRequired,
            t,
            titlesTranslate,
            formatDisplayDate
        ]
    )

    return (
        <TravellerReviewOverlay
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            isLoading={isLoading}
            deviceType={deviceType}
            contact={contact}
            travellerItems={travellerItems}
            titleLabel={t('itineraryPage.travelerDetailsRecord.reviewDetails')}
            contactSectionLabel={t('itineraryPage.contactDetails.contactDetails')}
            phoneLabel={t('itineraryPage.contactDetails.mobileNumber')}
            emailLabel={t('itineraryPage.contactDetails.emailAddress')}
            dobLabel={t('tripDetails.dateOfBirth')}
            passportNumberLabel={t('tripDetails.passportNumber')}
            expiryDateLabel={t('itineraryPage.travelerDetailsRecord.expiryDate')}
            continueToPaymentLabel={t('itineraryPage.travelerDetailsRecord.continueToPayment')}
        />
    )
}

export { TravellerReviewOverlayWrapper }
export default TravellerReviewOverlayWrapper
